import classNames from 'classnames'
import Link from 'next/link'
import BSAlert from 'react-bootstrap/Alert'
import Alert from 'react-bootstrap/Alert'
import BSCard from 'react-bootstrap/Card'
import YouTube from 'react-youtube'

import ArticlePreview from '@/components/ArticlePreview/ArticlePreview'
import Container from '@/components/Container/Container'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { useAds } from '@/contexts/ads'
import { CommandersPageProps } from '@/types/page'
import { BRACKETS } from '@/utils/constants'
import { asPartnerLink } from '@/utils/helpers'
import useDirectories from '@/utils/useDirectories'

import CoolHeader from '../CoolHeader/CoolHeader'
import Icon from '../Icon/Icon'

import BottomPanel from './BottomPanel/BottomPanel'
import CardPanel from './CardPanel/CardPanel'
import DeckSummaryPanel from './DeckSummaryPanel/DeckSummaryPanel'
import FilterPanel from './FilterPanel/FilterPanel'
import NavigationPanel from './NavigationPanel/NavigationPanel'
import styles from './Panels.module.scss'

type Props = {
  data: CommandersPageProps['data']
}

const Panels = ({ data }: Props) => {
  const { loadAds } = useAds()
  const { dir, subsubpage } = useDirectories()

  if (!data) return null

  const card = data?.container?.json_dict?.card
  const panels = data?.panels
  const isBracketSubpage = BRACKETS.includes(subsubpage)
  const isSoloPartnerPage =
    !card?.cards &&
    (card?.legal_partner ||
      card?.background_chooser ||
      card?.doctors_companion ||
      card?.type?.includes('Background') ||
      card?.type?.includes('Time Lord Doctor')) &&
    !['backgrounds', 'cards', 'partners'].includes(dir)
  const article = panels?.articles && panels.articles[0]
  const filtersEnabled = dir === 'commanders' || dir === 'decks'

  // Second article, for pages where filters is disabled
  const articleTwo = filtersEnabled ? null : panels?.articles && panels.articles[panels?.related_video?.youtube ? 0 : 1]

  // Hide the right panels if they're all empty
  const showRightPanels =
    data?.coalesce || panels?.related_video?.youtube || article || dir === 'deckpreview' || filtersEnabled || articleTwo

  // Bottom panel visibility
  const comboCounts = data.panels?.combocounts
  const deck = data.deck
  const description = data.description
  const manaCurve = data.panels?.mana_curve
  const pieChart = data.panels?.piechart
  const rankOverTime = data.panels?.rank_over_time
  const similar = data.similar
  const hasCharts = pieChart || manaCurve || rankOverTime
  const hasCombos = comboCounts?.length > 0
  const hasDeck = deck && (dir === 'average-decks' || dir === 'precon')
  const hasDescription = description?.length > 0
  const hasSimilar = similar?.length > 0
  const showBottomPanel = hasCharts || hasCombos || hasDeck || hasDescription || hasSimilar

  return (
    <Container>
      {isBracketSubpage && (
        <Alert className='mb-2' variant='warning'>
          <Icon className='me-2' icon='circleExclamation' />
          This page includes only decks with <b>user-set brackets</b>. Auto-detected brackets are not included.
        </Alert>
      )}
      <CoolHeader>{data.header}</CoolHeader>
      <div className={styles.container}>
        <div className={styles.panels}>
          {card && (
            <>
              {isSoloPartnerPage && (
                <BSAlert className='m-0 rounded-0 shadow-sm' variant='warning'>
                  This is {data?.container?.json_dict?.card.name}{' '}
                  <b>
                    with no <span>{card.background_chooser ? 'background' : 'partner'}</span>
                  </b>
                  . You may select a partner on the{' '}
                  <Link href={asPartnerLink(card)}>
                    <b>As Partner</b>
                  </Link>{' '}
                  page.
                </BSAlert>
              )}
              <div className={classNames(styles.row, { [styles.md]: loadAds !== true })}>
                <div className={classNames(styles.rowGroup, styles.left)}>
                  <CardPanel card={card} className={classNames({ ['rounded-bottom-left']: !showBottomPanel })} />
                  <NavigationPanel data={data} />
                </div>
                {showRightPanels && (
                  <div className={classNames(styles.rowGroup, styles.right)}>
                    {/* First article */}
                    {data?.coalesce ? (
                      <BSCard className={classNames(styles.content, 'rounded-0 shadow-sm')}>
                        <OutboundLink url={data.coalesce.product_url} utm_medium='coalesce_panel'>
                          <img alt={data.coalesce.product_name} className='w-100' src={data.coalesce.image} />
                        </OutboundLink>
                      </BSCard>
                    ) : panels?.related_video?.youtube ? (
                      <BSCard className={classNames(styles.content, 'rounded-0 shadow-sm')}>
                        <YouTube opts={{ height: 225, width: 400 }} videoId={panels?.related_video?.youtube} />
                      </BSCard>
                    ) : article ? (
                      <ArticlePreview article={article} small square />
                    ) : (
                      <div className={classNames(styles.content, 'd-none d-lg-block')} />
                    )}

                    {/* FilterPanel, DeckSummary, or second article */}
                    {dir === 'deckpreview' ? (
                      <DeckSummaryPanel data={data} />
                    ) : filtersEnabled ? (
                      <FilterPanel data={data} />
                    ) : articleTwo ? (
                      <div className='d-none d-lg-block h-100 w-100'>
                        <ArticlePreview article={articleTwo} small square />
                      </div>
                    ) : (
                      <div className={classNames(styles.content)} />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {showBottomPanel && <BottomPanel data={data} />}
        </div>
      </div>
    </Container>
  )
}

export default Panels
